/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import {
    Asset,
    AssetAssignment,
    AssetTemplate,
    AssetWithAssignment,
    Blank,
    CreateAssetRequest,
    Effect,
    Pagination,
    Setter,
    VersionedPagination,
} from "../../utils/Interfaces";
import { deleteHandler, getHandler, getHandlerVersionCaching, postHandler, stdErrHandlerFactory } from "../../utils/Requests";
import { AxiosResponse } from "axios";
import { setExistingAssetCatalog, setExistingAssets, setExistingAvailableAssets } from "./assetsSlice";
import { store } from "../../app/store";

export function listAssetsForUser(
    userId: number,
    dispatch: Dispatch<AnyAction>,
    onAssetsReturned: Setter<AssetAssignment[]>,
    setLoading: Setter<boolean>
): void {
    getHandler<Pagination<AssetAssignment>>(
        `/api/assets/list-by-user/${userId}`,
        dispatch,
        (response: AxiosResponse<Pagination<AssetAssignment>, any>) => onAssetsReturned(response.data.results),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function updateAssets(dispatch: Dispatch<AnyAction>, setLoading: Setter<boolean>): void {
    getHandlerVersionCaching<AssetWithAssignment>(
        "/api/assets/list/0",
        store.getState().assets.existingAssetsVersion,
        dispatch,
        (response: AxiosResponse<VersionedPagination<AssetWithAssignment>>) => dispatch(setExistingAssets(response.data)),
        () => {},
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function updateAvailableAssets(dispatch: Dispatch<AnyAction>, setLoading: Setter<boolean>): void {
    getHandlerVersionCaching<Asset>(
        "/api/assets/list-available",
        store.getState().assets.existingAssetsVersion,
        dispatch,
        (response: AxiosResponse<VersionedPagination<Asset>>) => dispatch(setExistingAvailableAssets(response.data)),
        () => {},
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function updateAssetsCatalog(dispatch: Dispatch<AnyAction>, setLoading: Setter<boolean>): void {
    getHandlerVersionCaching<AssetTemplate>(
        "/api/assets/catalog",
        store.getState().assets.existingAssetCatalogVersion,
        dispatch,
        (response: AxiosResponse<VersionedPagination<AssetTemplate>>) => dispatch(setExistingAssetCatalog(response.data)),
        () => {},
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function assignAsset(
    userId: number,
    assetId: number,
    dispatch: Dispatch<AnyAction>,
    onAssetAssigned: Effect,
    setLoading: Setter<boolean>
): void {
    postHandler<Blank, Blank>(
        `/api/assets/assign/${userId}/${assetId}`,
        {},
        dispatch,
        () => onAssetAssigned(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function unassignAsset(
    assetAssignmentId: number,
    dispatch: Dispatch<AnyAction>,
    onAssetUnassigned: Effect,
    setLoading: Setter<boolean>
): void {
    postHandler<Blank, Blank>(
        `/api/assets/unassign/${assetAssignmentId}`,
        {},
        dispatch,
        () => onAssetUnassigned(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function createAsset(
    payload: CreateAssetRequest,
    dispatch: Dispatch<AnyAction>,
    onAssetCreated: Effect,
    setLoading: Setter<boolean>
): void {
    postHandler<CreateAssetRequest, Blank>(
        "/api/assets/create",
        payload,
        dispatch,
        () => onAssetCreated(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function deleteAsset(assetId: number, dispatch: Dispatch<AnyAction>, onAssetDeleted: Effect, setLoading: Setter<boolean>) {
    deleteHandler<Blank>(
        `/api/assets/delete/${assetId}`,
        dispatch,
        () => {
            onAssetDeleted();
        },
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function modifyAsset(
    asset: Asset,
    dispatch: Dispatch<AnyAction>,
    onAssetUpdated: Effect,
    setLoading: Setter<boolean>
): void {
    // Make a deep copy of the asset and remove the ID, so it's a valid request item.
    let assetCopy: any = { ...asset };
    assetCopy.id = undefined;

    postHandler<Blank, Blank>(
        `/api/assets/update/${asset.id}`,
        assetCopy,
        dispatch,
        () => onAssetUpdated(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}
